import {
  formatDatetimeLocalForUser,
  getDateTimePartsInUserTz
} from './userTimezone';

/**
 * Persisted dashboard time range. Relative modes are recomputed on each load
 * in the user's timezone. Existing saved start/end values stay an absolute range.
 */
const STORAGE_KEY = 'commray_dashboard_timeRange';

export const DASHBOARD_TIME_PRESETS = [
  { id: '5m', label: 'Last 5 minutes', ms: 5 * 60 * 1000 },
  { id: '15m', label: 'Last 15 minutes', ms: 15 * 60 * 1000 },
  { id: '60m', label: 'Last 60 minutes', ms: 60 * 60 * 1000 },
  { id: '4h', label: 'Last 4 hours', ms: 4 * 60 * 60 * 1000 },
  { id: '24h', label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'today', label: 'Today (until now)' }
];

export const DASHBOARD_QUICK_AMOUNTS = Array.from({ length: 60 }, (_, i) => i + 1);

export const DASHBOARD_QUICK_UNITS = [
  { id: 'seconds', label: 'seconds', ms: 1000 },
  { id: 'minutes', label: 'minutes', ms: 60 * 1000 },
  { id: 'hours', label: 'hours', ms: 60 * 60 * 1000 },
  { id: 'days', label: 'days', ms: 24 * 60 * 60 * 1000 }
];

export const DASHBOARD_REFRESH_UNITS = DASHBOARD_QUICK_UNITS.filter((unit) => unit.id !== 'days');

const TIME_MODES = new Set(['preset', 'quick', 'absolute']);

function pad2(n) {
  return String(n).padStart(2, '0');
}

function shiftCalendarDate(year, month, day, deltaDays) {
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function clampAmount(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(60, Math.max(1, Math.round(n)));
}

const getDefaultRange = () => {
  const pad = (n) => String(n).padStart(2, '0');
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    startTime: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}`,
    endTime: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`
  };
};

export const emptyAbsoluteSelection = () => ({
  timeMode: 'absolute',
  timePreset: '15m',
  quickAmount: 15,
  quickUnit: 'minutes',
  refreshAmount: '',
  refreshUnit: 'minutes',
  startTime: '',
  endTime: ''
});

export function normalizeTimeSelection(raw) {
  const base = emptyAbsoluteSelection();
  const source = raw && typeof raw === 'object' ? raw : {};
  const quickUnit = DASHBOARD_QUICK_UNITS.some((unit) => unit.id === source.quickUnit)
    ? source.quickUnit
    : base.quickUnit;
  const refreshUnit = DASHBOARD_REFRESH_UNITS.some((unit) => unit.id === source.refreshUnit)
    ? source.refreshUnit
    : base.refreshUnit;
  const preset = DASHBOARD_TIME_PRESETS.some((item) => item.id === source.timePreset)
    ? source.timePreset
    : base.timePreset;
  return {
    timeMode: TIME_MODES.has(source.timeMode) ? source.timeMode : 'absolute',
    timePreset: preset,
    quickAmount: clampAmount(source.quickAmount, base.quickAmount),
    quickUnit,
    refreshAmount: source.refreshAmount === '' || source.refreshAmount == null
      ? ''
      : clampAmount(source.refreshAmount, ''),
    refreshUnit,
    startTime: source.startTime == null ? '' : String(source.startTime),
    endTime: source.endTime == null ? '' : String(source.endTime)
  };
}

function readStoredSelection() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object') {
        return normalizeTimeSelection(parsed);
      }
    }
  } catch (_) {}
  return normalizeTimeSelection({ timeMode: 'absolute', ...getDefaultRange() });
}

export const loadTimeSelection = (fromState) => {
  const stored = readStoredSelection();
  if (!fromState || typeof fromState !== 'object') {
    return stored;
  }
  const hasMode = TIME_MODES.has(fromState.timeMode);
  const hasBounds = Boolean(fromState.startTime || fromState.endTime);
  if (!hasMode && !hasBounds) {
    return stored;
  }
  return normalizeTimeSelection({
    ...stored,
    ...fromState,
    timeMode: hasMode ? fromState.timeMode : 'absolute'
  });
};

export const saveTimeSelection = (selection) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeTimeSelection(selection)));
  } catch (_) {}
};

/** Clear persisted Start/End filters (empty = use plan retention window). */
export const clearTimeSelection = () => {
  const cleared = emptyAbsoluteSelection();
  saveTimeSelection(cleared);
  return cleared;
};

export const loadTimeRange = (fromState) => {
  const selection = loadTimeSelection(fromState);
  return { startTime: selection.startTime, endTime: selection.endTime };
};

export const saveTimeRange = (startTime, endTime) => {
  const current = readStoredSelection();
  saveTimeSelection({ ...current, timeMode: 'absolute', startTime, endTime });
};

export const clearTimeRange = () => {
  const cleared = clearTimeSelection();
  return { startTime: cleared.startTime, endTime: cleared.endTime };
};

/** Resolve preset / quick-select windows in the user's timezone. Absolute mode keeps the typed start/end. */
export function resolveDashboardTimeWindow(selection, appTimezone) {
  const now = new Date();
  const endNow = formatDatetimeLocalForUser(now, appTimezone);
  const mode = selection?.timeMode || 'absolute';
  if (mode === 'absolute') {
    return { startTime: selection?.startTime || '', endTime: selection?.endTime || '' };
  }
  if (mode === 'quick') {
    const amount = clampAmount(selection?.quickAmount, 15);
    const unit = DASHBOARD_QUICK_UNITS.find((item) => item.id === selection?.quickUnit) || DASHBOARD_QUICK_UNITS[1];
    return {
      startTime: formatDatetimeLocalForUser(new Date(now.getTime() - amount * unit.ms), appTimezone),
      endTime: endNow
    };
  }
  const preset = DASHBOARD_TIME_PRESETS.find((item) => item.id === (selection?.timePreset || '15m'))
    || DASHBOARD_TIME_PRESETS[1];
  if (preset.ms) {
    return {
      startTime: formatDatetimeLocalForUser(new Date(now.getTime() - preset.ms), appTimezone),
      endTime: endNow
    };
  }
  const parts = getDateTimePartsInUserTz(now, appTimezone);
  if (!parts) {
    return { startTime: '', endTime: endNow };
  }
  const today = `${parts.y}-${pad2(parts.mo)}-${pad2(parts.da)}`;
  if (preset.id === 'today') {
    return { startTime: `${today}T00:00:00.000`, endTime: endNow };
  }
  const yesterday = shiftCalendarDate(parts.y, parts.mo, parts.da, -1);
  return { startTime: `${yesterday}T00:00:00.000`, endTime: `${yesterday}T23:59:59.999` };
}
